/*
 * AccountPanel: the signed-in person's own account, opened from their name in
 * the sidebar. What it holds is their email, which is where a link to choose a
 * new password goes when they forget theirs (server/pkg/portalauth).
 *
 * A new address is proved before it is kept: the current password asks for a
 * code, the code goes to the new address, and the account changes only when it
 * comes back. A wrong password is an answer, not a session ending, so nothing
 * here signs anyone out or hands the session back to the gate.
 *
 * Styling reuses the .ac-* block of AppLayout.css (Access Control); wording is
 * in accessWords.js.
 */
import React, { useState } from "react";
import { FiMail } from "react-icons/fi";
import { askServer, typedCode, useSession } from "./PortalGate";
import * as W from "./accessWords";

export default function AccountPanel() {
  const session = useSession();
  const { user, sendsEmail } = session;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  // {ticket, email} once a code has gone to the new address.
  const [sent, setSent] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const start = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMsg(null);
    const r = await askServer("/auth/email", { email: email.trim(), password });
    setBusy(false);
    setPassword("");
    if (r.ok) setSent(r.data);
    else setMsg({ ok: false, text: W.refusal(r) });
  };

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMsg(null);
    const r = await askServer("/auth/email/verify", { ticket: sent.ticket, code });
    setBusy(false);
    setCode("");
    if (r.ok) {
      setSent(null);
      setEmail("");
      setMsg({ ok: true, text: W.emailNow(r.data.user.email) });
      session.refresh();
      return;
    }
    // A wrong code may be typed again. Anything else (out of time, too many
    // wrong codes, the address already on another account) starts again.
    if (r.status !== 400) setSent(null);
    setMsg({ ok: false, text: W.refusal(r) });
  };

  const back = () => {
    setSent(null);
    setMsg(null);
  };

  return (
    <div className="ac-stack">
      <div className="panel">
        <div className="panel-title">
          <FiMail style={{ verticalAlign: "-2px", marginRight: 8 }} />
          {W.EMAIL_TITLE}
        </div>
        <div className={`ac-value ${user.email ? "" : "none"}`}>{user.email || W.NO_EMAIL}</div>
        <div className="panel-sub">{W.emailWhy(!!user.email, sendsEmail)}</div>

        {sendsEmail &&
          (sent ? (
            <form onSubmit={verify} className="ac-newform">
              <div className="ac-blurb">{W.emailCodeSent(sent.email)}</div>
              <div className="ac-form">
                <input
                  className="ac-input narrow ac-code"
                  value={code}
                  onChange={(e) => setCode(typedCode(e.target.value))}
                  placeholder="123456"
                  aria-label={W.CODE}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                />
                <button type="submit" className="ac-btn primary" disabled={busy || code.length < 6}>
                  {W.CONFIRM}
                </button>
                <button type="button" className="ac-btn" onClick={back} disabled={busy}>
                  {W.BACK}
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={start} className="ac-newform">
              <div className="ac-grid">
                <label className="ac-field">
                  <span>{W.NEW_EMAIL}</span>
                  <input
                    className="ac-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    autoComplete="email"
                  />
                </label>
                <label className="ac-field">
                  <span>{W.CURRENT_PASSWORD}</span>
                  <input
                    className="ac-input"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                  />
                </label>
              </div>
              <div className="ac-blurb">{W.CHANGE_HINT}</div>
              <div>
                <button
                  type="submit"
                  className="ac-btn primary"
                  disabled={busy || !email.trim() || !password}
                >
                  {busy ? W.SENDING : W.SEND_CODE}
                </button>
              </div>
            </form>
          ))}

        {msg && (
          <div className={msg.ok ? "ac-notice" : "panel-error"} role={msg.ok ? "status" : "alert"}>
            {msg.text}
          </div>
        )}
      </div>
    </div>
  );
}
